/**
 * Sport-specific analytics dashboards with tab selector.
 * Renders cycling, crossfit, hyrox, strength, or multi-sport dashboard
 * based on the selected sport tab.
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { sportConfig } from "@/lib/coachConstants";
import { useProgression } from "@/hooks/useProgression";
import { useRecentPRs } from "@/hooks/useBenchmarks";
import type { CompletedActivity } from "@/types";
import { Loader2 } from "lucide-react";

// Sport-specific dashboard components
import { FTPTrendChart } from "./FTPTrendChart";
import { CyclingTSSCalendar } from "./CyclingTSSCalendar";
import { BenchmarkWODTracker } from "./BenchmarkWODTracker";
import { MovementPRBoard } from "./MovementPRBoard";
import { WODHistoryList } from "./WODHistoryList";
import { StationBreakdownChart } from "./StationBreakdownChart";
import { HyroxRaceTimeline } from "./HyroxRaceTimeline";
import { HyroxProgressionChart } from "./HyroxProgressionChart";
import { ExercisePRChart } from "./ExercisePRChart";
import { VolumeLoadChart } from "./VolumeLoadChart";
import { SportDistributionChart } from "./SportDistributionChart";
import { CombinedLoadChart } from "./CombinedLoadChart";

type SportTab = "all" | "running" | "cycling" | "crossfit" | "strength" | "hyrox" | "multi-sport";

const SPORT_TABS: { key: SportTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "running", label: "Running" },
  { key: "cycling", label: "Cycling" },
  { key: "crossfit", label: "CrossFit" },
  { key: "strength", label: "Strength" },
  { key: "hyrox", label: "Hyrox" },
  { key: "multi-sport", label: "Multi-Sport" },
];

const PROGRESSION_SPORTS: SportTab[] = ["running", "cycling", "crossfit", "strength", "hyrox"];

interface SportDashboardsProps {
  activities: CompletedActivity[];
}

export function SportDashboards({ activities }: SportDashboardsProps) {
  const [activeTab, setActiveTab] = useState<SportTab>("all");

  const progressionSport = PROGRESSION_SPORTS.includes(activeTab) ? activeTab : null;
  const { data: progression, isLoading: progressionLoading } = useProgression(progressionSport);
  const { data: recentPRs } = useRecentPRs(20);

  const sportActivities = useMemo(() => {
    if (activeTab === "all" || activeTab === "multi-sport") return activities;
    return activities.filter((a) => a.sport === activeTab);
  }, [activities, activeTab]);

  const sportCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of activities) {
      const s = a.sport ?? "other";
      counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return counts;
  }, [activities]);

  return (
    <div className="space-y-5 pt-4 border-t border-border/30">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
          ▸ Sport-Specific Analytics
        </h2>
        <div className="text-[10px] font-mono text-muted-foreground">
          {sportActivities.length} activities
        </div>
      </div>

      {/* Sport tabs */}
      <div className="flex flex-wrap gap-1">
        {SPORT_TABS.map((tab) => {
          const count = tab.key === "all"
            ? activities.length
            : tab.key === "multi-sport"
              ? activities.length
              : sportCount.get(tab.key) ?? 0;
          const cfg = sportConfig[tab.key as keyof typeof sportConfig];

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "px-3 py-1.5 rounded text-xs font-mono tracking-wider uppercase transition-all border",
                activeTab === tab.key
                  ? "bg-[rgba(0,255,200,0.12)] text-[#00FFC8] border-[rgba(0,255,200,0.3)]"
                  : "text-muted-foreground border-white/[0.06] hover:border-white/[0.12]",
              )}
            >
              {tab.label}
              {count > 0 && tab.key !== "all" && (
                <span className="ml-1 text-[9px] opacity-60">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sport dashboard content */}
      {activeTab === "all" && (
        <div className="text-xs text-muted-foreground">
          Select a sport tab above to view sport-specific progression and analytics.
        </div>
      )}

      {progressionLoading && progressionSport && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {activeTab === "running" && progression?.data && (
        <RunningDashboard data={progression.data} />
      )}

      {activeTab === "cycling" && progression?.data && (
        <CyclingDashboard data={progression.data} />
      )}

      {activeTab === "crossfit" && progression?.data && (
        <CrossFitDashboard data={progression.data} prs={recentPRs?.prs ?? []} />
      )}

      {activeTab === "strength" && progression?.data && (
        <StrengthDashboard data={progression.data} />
      )}

      {activeTab === "hyrox" && progression?.data && (
        <HyroxDashboard data={progression.data} />
      )}

      {activeTab === "multi-sport" && (
        <MultiSportDashboard activities={activities} />
      )}
    </div>
  );
}

// ── Running Dashboard ─────────────────────────────────────────────────────

function RunningDashboard({ data }: { data: Record<string, unknown> }) {
  const vdot = typeof data.vdot === "number" ? (data.vdot as number).toFixed(1) : null;
  const pacePerKm = typeof data.pace_per_km === "number" ? data.pace_per_km : null;
  const predictions = data.race_predictions as Record<string, number> | undefined;

  const formatPace = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="VDOT" value={vdot ?? "—"} />
        <KpiCard label="Pace/km" value={pacePerKm ? formatPace(pacePerKm as number) : "—"} />
        {predictions?.["5000"] && <KpiCard label="5K Pred" value={formatPace(predictions["5000"])} />}
        {predictions?.["42195"] && <KpiCard label="Marathon Pred" value={formatPace(predictions["42195"])} />}
      </div>

      {/* Race PRs */}
      {data.race_prs && (
        <div className="rounded-xl border border-border/60 p-4">
          <h3 className="text-sm font-semibold mb-3">Race PRs</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(data.race_prs as Record<string, { value?: number; date?: string }>).map(
              ([distance, pr]) => (
                <div key={distance} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 text-center">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{distance}</div>
                  <div className="text-sm font-bold tabular-nums text-foreground">
                    {pr.value ? formatPace(pr.value) : "—"}
                  </div>
                  {pr.date && <div className="text-[9px] text-muted-foreground mt-0.5">{pr.date}</div>}
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Cycling Dashboard ─────────────────────────────────────────────────────

function CyclingDashboard({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <FTPTrendChart data={data} />
        <CyclingTSSCalendar data={data} />
      </section>
    </div>
  );
}

// ── CrossFit Dashboard ────────────────────────────────────────────────────

function CrossFitDashboard({ data, prs }: { data: Record<string, unknown>; prs: Array<{ benchmark_name: string; value: number; unit: string; benchmark_type: string; recorded_at: string; sport: string | null }> }) {
  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BenchmarkWODTracker data={data} />
        <MovementPRBoard data={data} />
      </section>
      <WODHistoryList prs={prs} />
    </div>
  );
}

// ── Strength Dashboard ────────────────────────────────────────────────────

function StrengthDashboard({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-4">
      <ExercisePRChart data={data} />
      <VolumeLoadChart data={data} />
    </div>
  );
}

// ── Hyrox Dashboard ───────────────────────────────────────────────────────

function HyroxDashboard({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-4">
      <HyroxRaceTimeline data={data} />
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <StationBreakdownChart data={data} />
        <HyroxProgressionChart data={data} />
      </section>
    </div>
  );
}

// ── Multi-Sport Dashboard ─────────────────────────────────────────────────

function MultiSportDashboard({ activities }: { activities: CompletedActivity[] }) {
  return (
    <div className="space-y-4">
      <SportDistributionChart activities={activities} />
      <CombinedLoadChart data={[]} freshnessStatus="neutral" />
    </div>
  );
}

// ── Shared KPI Card ───────────────────────────────────────────────────────

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 p-3 text-center">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className="text-lg font-bold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
